import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../lib/api';
import { useAuth } from '../lib/authContext';
import { ProfileLockedCard } from '../components/ProfileLockedCard';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { ListingStatusBadge } from '@siliconx/shared/ui/ListingStatusBadge';
import { formatDate } from '../lib/format';
import type { Listing, SkuListingOption } from '@siliconx/shared/dto/listing';
import { qk } from '../lib/queryKeys';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { PassportListingEndPrompt, isPassportListingEnd } from '../components/asset-detail/PassportListingEndPrompt';

export const Route = createFileRoute('/listings')({
  component: ListingsPage,
});

const TYPE_BADGE = {
  sku: { label: 'SKU', cls: 'bg-violet-100 text-violet-700' },
  lot: { label: 'Lot', cls: 'bg-emerald-100 text-emerald-700' },
  asset: { label: 'Asset', cls: 'bg-sky-100 text-sky-700' },
} as const;

function TypeBadge({ type }: { type: string }) {
  const { label, cls } = TYPE_BADGE[type as keyof typeof TYPE_BADGE] ?? TYPE_BADGE.asset;
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

function ListingActions({ listing }: { listing: Listing }) {
  const queryClient = useQueryClient();

  const activate = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings[':id'].activate.$post({ param: { id: listing.id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Listing activated.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Activate failed.'),
  });

  const withdraw = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings[':id'].withdraw.$post({ param: { id: listing.id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Listing withdrawn.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Withdraw failed.'),
  });

  const canActivate = listing.status === 'draft' || listing.status === 'withdrawn';
  const canWithdraw =
    listing.status === 'draft' ||
    listing.status === 'active' ||
    listing.status === 'under_offer';

  return (
    <div className="flex flex-wrap gap-2">
      {/* SIL-2409: a cancelled (withdrawn) or expired asset listing asks an admin whether to make the passport private again. */}
      {isPassportListingEnd(listing) && <PassportListingEndPrompt assetId={listing.assetId!} />}
      {canActivate && (
        <button
          type="button"
          onClick={() => activate.mutate()}
          disabled={activate.isPending}
          className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
        >
          {activate.isPending ? '…' : 'Activate'}
        </button>
      )}
      {canWithdraw && (
        <button
          type="button"
          onClick={() => withdraw.mutate()}
          disabled={withdraw.isPending}
          className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          {withdraw.isPending ? '…' : 'Withdraw'}
        </button>
      )}
    </div>
  );
}

const COLUMNS: ColumnDef<Listing, unknown>[] = [
  {
    accessorKey: 'listingType',
    header: 'Type',
    cell: (ctx) => <TypeBadge type={ctx.getValue<string>()} />,
  },
  {
    // Assets and lots share this collection, and a lot can span multiple
    // manufacturers/models — so a single self-contained "Description" (make +
    // title) is honest where separate Make/Model columns were not. Matches the
    // ops listings table. Lots carry the lot name in displayTitle with no
    // single make; asset/sku carry the mfr-stripped title, re-prefixed here.
    id: 'description',
    header: 'Description',
    cell: (ctx) => {
      const l = ctx.row.original;
      const label =
        l.make && l.displayTitle
          ? `${l.make} ${l.displayTitle}`
          : (l.displayTitle ?? l.make ?? '—');
      return <span className="text-sm text-zinc-700">{label}</span>;
    },
  },
  {
    accessorKey: 'quantity',
    header: 'Qty',
    cell: (ctx) => (
      <span className="text-sm text-zinc-700">{ctx.getValue<number | null>() ?? '1'}</span>
    ),
  },
  {
    accessorKey: 'askingPriceUsd',
    header: 'Asking price',
    cell: (ctx) => <UsdCell value={ctx.getValue<number>()} />,
  },
  {
    // SIL-643: sale mechanic + (auction-only) close time + reserve. This
    // route (GET /api/client/listings) only ever returns the caller's OWN
    // listings, and reservePriceUsd is sourced server-side via
    // client_listing_auction_reserve_price() (759's SECURITY DEFINER
    // accessor) — safe to render unconditionally here.
    id: 'saleMechanic',
    header: 'Mechanic',
    cell: (ctx) => {
      const l = ctx.row.original;
      if (l.saleMechanic !== 'auction') {
        return <span className="text-sm text-zinc-500">Buy now</span>;
      }
      return (
        <div className="text-sm">
          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Auction
          </span>
          {l.auction && (
            <div className="mt-0.5 text-xs text-zinc-500">
              Closes {formatDate(l.auction.closesAt)}
            </div>
          )}
          {l.reservePriceUsd != null && (
            <div className="mt-0.5 text-xs text-zinc-500">
              Reserve: <UsdCell value={l.reservePriceUsd} className="text-xs" />
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: (ctx) => (
      <ListingStatusBadge value={ctx.getValue<string>()} availableOn={ctx.row.original.availableOn} />
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: (ctx) => (
      <span className="text-sm text-zinc-700">{formatDate(ctx.getValue<string>())}</span>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: (ctx) => <ListingActions listing={ctx.row.original} />,
  },
];

interface ListingsResponse {
  data: Listing[];
}

interface SkuOptionsResponse {
  data: SkuListingOption[];
}

// ---------------------------------------------------------------------------
// Create a fungible SKU listing (RUF-309) — sku picker + quantity + price.
// ---------------------------------------------------------------------------
function CreateSkuListing() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [skuId, setSkuId] = useState('');
  const [qtyStr, setQtyStr] = useState('1');
  const [priceStr, setPriceStr] = useState('');

  const { data: options } = useQuery<SkuOptionsResponse>({
    queryKey: qk.listings.skuOptions(),
    queryFn: async () => {
      const res = await api.api.client.listings['sku-options'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as SkuOptionsResponse;
    },
    enabled: open,
  });

  const selected = useMemo(
    () => options?.data.find((o) => o.skuId === skuId) ?? null,
    [options, skuId],
  );

  function selectSku(id: string) {
    setSkuId(id);
    const opt = options?.data.find((o) => o.skuId === id) ?? null;
    setPriceStr(opt?.marketCompPriceUsd != null ? String(opt.marketCompPriceUsd) : '');
    setQtyStr('1');
  }

  const mutation = useMutation({
    mutationFn: async (payload: {
      listingType: 'sku';
      skuId: string;
      quantity: number;
      askingPriceUsd?: number;
    }) => {
      const res = await api.api.client.listings.$post({ json: payload });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('SKU listing created as a draft. Activate it to publish.');
      setOpen(false);
      setSkuId('');
      setQtyStr('1');
      setPriceStr('');
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not create SKU listing.'),
  });

  function handleCreate() {
    if (!selected) {
      toast.error('Pick a SKU first.');
      return;
    }
    const qty = Number(qtyStr);
    if (!Number.isInteger(qty) || qty <= 0) {
      toast.error('Quantity must be a positive whole number.');
      return;
    }
    if (qty > selected.eligibleQuantity) {
      toast.error(`You only have ${selected.eligibleQuantity} listable unit(s) of this SKU.`);
      return;
    }
    const parsedPrice = priceStr.trim() !== '' ? Number(priceStr) : undefined;
    if (parsedPrice !== undefined && (isNaN(parsedPrice) || parsedPrice <= 0)) {
      toast.error('Asking price must be a positive number.');
      return;
    }
    mutation.mutate({
      listingType: 'sku',
      skuId: selected.skuId,
      quantity: qty,
      askingPriceUsd: parsedPrice,
    });
  }

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
      >
        List a SKU
      </Button>
    );
  }

  const opts = options?.data ?? [];

  return (
    <Card>
      <h2 className="text-base font-semibold text-zinc-900">List a SKU quantity</h2>
      <p className="mt-1 text-sm text-zinc-500">
        Sell a quantity of a SKU you have listable inventory for. The price is pre-filled
        with the market-comp median.
      </p>

      {opts.length === 0 ? (
        <p className="mt-4 text-sm text-zinc-400">
          You have no SKUs with listable inventory yet.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500">
              SKU
            </label>
            <select
              value={skuId}
              onChange={(e) => selectSku(e.target.value)}
              className="mt-1 w-full max-w-md rounded border border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Select a SKU…</option>
              {opts.map((o) => (
                <option key={o.skuId} value={o.skuId}>
                  {[o.make, o.model].filter(Boolean).join(' ') ||
                    o.shortDescription ||
                    o.skuId.slice(0, 8)}
                  {` — ${o.eligibleQuantity} available`}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                step="1"
                max={selected?.eligibleQuantity ?? undefined}
                value={qtyStr}
                onChange={(e) => setQtyStr(e.target.value)}
                className="mt-1 w-full rounded border border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 focus:border-blue-500 focus:outline-none"
              />
              {selected && (
                <p className="mt-1 text-xs text-zinc-400">
                  {selected.eligibleQuantity} listable unit(s) available.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500">
                Asking price (USD)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={priceStr}
                onChange={(e) => setPriceStr(e.target.value)}
                placeholder="market-comp median"
                className="mt-1 w-full rounded border border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 focus:border-blue-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-zinc-400">
                Pre-filled with the market-comp median. Leave as-is or override.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleCreate}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Creating…' : 'Create SKU listing'}
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
              className="text-sm text-zinc-500 hover:text-zinc-700 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function ListingsPage() {
  const navigate = useNavigate();
  const { canSell, sellerMissingFields, organizationType, individualProfileMissing } = useAuth();
  // SIL-1132: an individual org's canSell is gated by individual_profile
  // completeness (unlock together with canBuy), not seller_org — the
  // "complete profile" CTA below must point at /individual-profile with
  // individualProfileMissing, not /profile with the (always-empty, for an
  // individual org) sellerMissingFields.
  const isIndividual = organizationType === 'individual';
  const { data, isFetching } = useQuery<ListingsResponse>({
    queryKey: qk.listings.all,
    queryFn: async () => {
      const res = await api.api.client.listings.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ListingsResponse;
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <PageHeader title="My Listings" pinTo="/listings" />
          <p className="text-sm text-zinc-500">
            List eligible assets or SKU quantities for sale, then activate them so buyers can
            see them on the exchange.
          </p>
        </div>
        {/* RUF-540: existing listings (created before the profile went
            incomplete) still show below — only the create action is gated. */}
        {canSell && <CreateSkuListing />}
      </div>
      {!canSell && (
        <ProfileLockedCard
          side="seller"
          missingFields={isIndividual ? individualProfileMissing : sellerMissingFields}
          href={isIndividual ? '/individual-profile' : '/profile'}
          individual={isIndividual}
        />
      )}
      <DataTable
        data={data?.data ?? []}
        columns={COLUMNS}
        onRowClick={(listing) =>
          void navigate({
            to: '/listings/$listingId',
            params: { listingId: listing.id },
          })
        }
        emptyMessage={isFetching ? 'Loading…' : 'No listings yet.'}
      />
    </div>
  );
}
